import API_BASE from '../config';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MDEditor from '@uiw/react-md-editor';
import toast from "react-hot-toast";
import { creditAwareFetch } from '../hooks/creditAwareFetch';

const FIELDS = [
  { key: 'best_clients', label: 'Best clients', placeholder: 'Describe their ideal clients — who they love working with, what makes a great fit.' },
  { key: 'worst_clients', label: 'Worst clients', placeholder: 'Who are the wrong clients? What makes a bad fit or a difficult engagement?' },
  { key: 'previous_attempts', label: 'Previous outreach attempts', placeholder: 'What have they tried before? What worked, what did not?' },
  { key: 'sales_cycle', label: 'Sales cycle', placeholder: 'How long does it typically take from first contact to close? What are the stages?' },
  { key: 'sensitivities', label: 'Sensitivities', placeholder: 'Any topics, sectors, or approaches to avoid? Anything politically sensitive?' },
  { key: 'tone_preferences', label: 'Tone preferences', placeholder: 'How do they want to come across in outreach? Formal, conversational, direct?' },
];


function DiscoveryBrief() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [formData, setFormData] = useState({});
  const [editingField, setEditingField] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [nlPrompt, setNlPrompt] = useState('');
  const [generating, setGenerating] = useState(false);

  useEffect(() => { fetchClient(); fetchBrief(); }, [id]);

  async function fetchClient() {
    try {
      const res = await fetch(`${API_BASE}/clients/${id}`);
      setClient(await res.json());
    } catch (err) { setError('Could not load client.'); }
  }

  async function fetchBrief() {
    try {
      const res = await fetch(`${API_BASE}/clients/${id}/discovery`);
      const data = await res.json();
      if (data) setFormData({
        best_clients: data.best_clients || '',
        worst_clients: data.worst_clients || '',
        previous_attempts: data.previous_attempts || '',
        sales_cycle: data.sales_cycle || '',
        sensitivities: data.sensitivities || '',
        tone_preferences: data.tone_preferences || '',
      });
    } catch (err) {}
  }

  async function handleGenerate() {
    if (!nlPrompt.trim()) return;
    setGenerating(true);
    setError(null);
    const toastId = toast.loading('Generating discovery brief with AI...');
    try {
      const res = await creditAwareFetch(`${API_BASE}/clients/${id}/discovery/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: nlPrompt })
      });
      if (!res) return;
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setFormData(prev => ({
        best_clients: data.best_clients || prev.best_clients,
        worst_clients: data.worst_clients || prev.worst_clients,
        previous_attempts: data.previous_attempts || prev.previous_attempts,
        sales_cycle: data.sales_cycle || prev.sales_cycle,
        sensitivities: data.sensitivities || prev.sensitivities,
        tone_preferences: data.tone_preferences || prev.tone_preferences,
      }));
      setNlPrompt('');
      toast.success('Discovery brief generated', { id: toastId });
    } catch (err) {
      setError('Could not generate: ' + err.message);
      toast.error('Could not generate: ' + err.message, { id: toastId });
    }
    setGenerating(false);
  }

  async function handleSave() {
    setSaving(true);
    try {
      await fetch(`${API_BASE}/clients/${id}/discovery`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      setEditingField(null);
      toast.success('Discovery brief saved');
      navigate('/clients/' + id);
    } catch (err) {
      setError('Could not save brief.');
      toast.error('Could not save brief');
    }
    setSaving(false);
  }

  async function handleFieldSave() {
    try {
      await fetch(`${API_BASE}/clients/${id}/discovery`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      setEditingField(null);
    } catch (err) { setError('Could not save.'); }
  }

  return (
    <div style={{ backgroundColor: '#FAFAF8', minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }} data-color-mode="light">

      <div style={{ padding: '2rem', maxWidth: '900px', margin: '0 auto', boxSizing: 'border-box' }}>

        <button onClick={() => navigate('/clients/' + id)} style={{ background: 'none', border: 'none', color: '#888', fontSize: '0.83rem', cursor: 'pointer', padding: 0, marginBottom: '1.5rem' }}>
          ← {client ? client.name : 'Back'}
        </button>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '2rem' }}>
          <div>
            <h1 style={{ fontSize: '1.4rem', fontWeight: '500', margin: '0 0 0.25rem', color: '#1A1A1A' }}>Discovery Brief</h1>
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#888' }}>{client ? client.name : ''}</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {error && <span style={{ fontSize: '0.83rem', color: '#C53030' }}>{error}</span>}
            <button onClick={handleSave} disabled={saving} style={{ backgroundColor: '#8B1A1A', color: 'white', border: 'none', borderRadius: '6px', padding: '0.6rem 1.2rem', fontSize: '0.875rem', cursor: 'pointer', fontWeight: '500', opacity: saving ? 0.5 : 1 }}>
              {saving ? 'Saving...' : 'Save and return'}
            </button>
          </div>
        </div>

        {/* AI fill panel */}
        <div style={{ backgroundColor: '#fff', border: '0.5px solid #E8E4E0', borderLeft: '3px solid #1A56A0', borderRadius: '10px', padding: '1.25rem', marginBottom: '1.5rem' }}>
          <p style={{ margin: '0 0 0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#1A1A1A' }}>Fill from description</p>
          <p style={{ margin: '0 0 0.75rem', fontSize: '0.8rem', color: '#888' }}>Describe the client in plain English and the AI will fill in the fields. You can edit them afterwards.</p>
          <textarea
            value={nlPrompt}
            onChange={e => setNlPrompt(e.target.value)}
            placeholder="e.g. Fadac Resources is a Nigerian recruitment firm targeting mid-size companies, conservative and professional tone, they have tried cold calling before without success, sales cycle is typically 2-3 months..."
            rows={3}
            style={{ width: '100%', backgroundColor: '#FAFAF8', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.75rem', color: '#1A1A1A', fontSize: '0.85rem', boxSizing: 'border-box', resize: 'vertical', lineHeight: '1.5', fontFamily: 'inherit', marginBottom: '0.75rem', outline: 'none' }}
          />
          <button onClick={handleGenerate} disabled={generating || !nlPrompt.trim()}
            style={{ backgroundColor: '#1A56A0', color: 'white', border: 'none', borderRadius: '6px', padding: '0.5rem 1rem', fontSize: '0.85rem', cursor: 'pointer', fontWeight: '500', opacity: generating || !nlPrompt.trim() ? 0.5 : 1 }}>
            {generating ? 'Generating...' : 'Fill fields with AI'}
          </button>
        </div>

        {/* Fields */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', paddingBottom: '3rem' }}>
          {FIELDS.map(field => (
            <div key={field.key} style={{ backgroundColor: '#fff', border: '0.5px solid #E8E4E0', borderLeft: '3px solid #8B1A1A', borderRadius: '10px', padding: '1.25rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <label style={{ fontSize: '0.83rem', color: '#4A5568', fontWeight: '500' }}>{field.label}</label>
                {editingField !== field.key ? (
                  <button onClick={() => setEditingField(field.key)}
                    style={{ background: 'none', border: '1px solid #D4CFC9', borderRadius: '20px', color: '#4A5568', fontSize: '0.75rem', padding: '0.2rem 0.6rem', cursor: 'pointer' }}>
                    Edit
                  </button>
                ) : (
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button onClick={handleFieldSave}
                      style={{ background: '#276749', border: 'none', borderRadius: '20px', color: 'white', fontSize: '0.75rem', padding: '0.2rem 0.6rem', cursor: 'pointer', fontWeight: '500' }}>
                      Done
                    </button>
                    <button onClick={() => setEditingField(null)}
                      style={{ background: 'none', border: '1px solid #D4CFC9', borderRadius: '20px', color: '#888', fontSize: '0.75rem', padding: '0.2rem 0.6rem', cursor: 'pointer' }}>
                      Cancel
                    </button>
                  </div>
                )}
              </div>
              {editingField === field.key ? (
                <MDEditor
                  value={formData[field.key] || ''}
                  onChange={val => setFormData(prev => ({ ...prev, [field.key]: val || '' }))}
                  preview="edit"
                  height={200}
                />
              ) : (
                <div style={{ fontSize: '0.875rem', color: formData[field.key] ? '#1A1A1A' : '#BBB', lineHeight: '1.6', whiteSpace: 'pre-wrap', minHeight: '1.5rem' }}>
                  {formData[field.key] || field.placeholder}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default DiscoveryBrief;
